import { useMemo, useState } from "react";
import type { DataSnapshot, SkillHistory, HistoryEntry } from "../lib/types";
import { formatTimestamp, relativeDays } from "../lib/format";
import { BrandRail } from "../components/BrandRail";
import { scoreClass, scoreClassText } from "../lib/scoring";
import { navigateToSkill } from "../router";

interface Props { data: DataSnapshot }

export function History({ data }: Props) {
  const { history, stats } = data;
  const [view, setView] = useState<"by-run" | "by-skill">("by-run");

  const openSkill = (name: string) => {
    navigateToSkill(name);
  };

  const allEntries = useMemo<Array<HistoryEntry & { skill: string }>>(
    () => history.flatMap((h) => h.entries.map((e) => ({ ...e, skill: h.skill }))),
    [history]
  );

  const sortedByRun = useMemo(
    () => [...allEntries].sort((a, b) => b.run - a.run),
    [allEntries]
  );

  const totalRuns = allEntries.length;
  const measured = allEntries.filter((e) => e.status === "measured").length;
  const kept = allEntries.filter((e) => e.status === "keep").length;
  const discarded = allEntries.filter((e) => e.status === "discard").length;
  const crashed = allEntries.filter((e) => e.status === "crash").length;

  return (
    <div className="grid min-h-screen" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{
        monitor: stats.total,
        issues: (stats.statusCounts?.warning ?? 0) + (stats.statusCounts?.critical ?? 0),
      }} />

      <main className="min-w-0">
        <header className="px-6 pt-6 pb-4 border-b border-fg">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              // Autoresearch metric · last {formatTimestamp(stats.lastRunAt)}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              {history.length} skills · {totalRuns} entries
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <h1 className="font-display font-extrabold leading-[0.86] tracking-[-0.045em] text-t-72">
              History
            </h1>
            <div className="flex flex-col gap-1 pb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
                Run-by-run audit trail
              </span>
              <span className="font-mono text-[14px] uppercase tracking-[0.12em] text-fg">
                {kept}/{totalRuns} <span className="text-good">kept</span>
                {discarded > 0 && <> · {discarded} <span className="text-bad">discarded</span></>}
                {crashed > 0 && <> · {crashed} <span className="text-warn">crashed</span></>}
              </span>
            </div>
            <div className="ml-auto flex border border-fg self-end">
              <button
                onClick={() => setView("by-run")}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] border-r border-fg font-medium ${
                  view === "by-run" ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
                }`}
              >
                By run
              </button>
              <button
                onClick={() => setView("by-skill")}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] font-medium ${
                  view === "by-skill" ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
                }`}
              >
                By skill
              </button>
            </div>
          </div>

          <div className="editorial-rule mt-4" />
        </header>

        <div className="p-6">
          {allEntries.length === 0 ? (
            <EmptyState />
          ) : view === "by-run" ? (
            <RunsView entries={sortedByRun} onOpenSkill={openSkill} />
          ) : (
            <SkillsView history={history} onOpenSkill={openSkill} />
          )}
          {allEntries.length > 0 && <StatusNote measured={measured} kept={kept} discarded={discarded} crashed={crashed} total={totalRuns} />}
        </div>
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-24">
      <h3 className="font-display font-extrabold text-t-40 leading-[1] tracking-[-0.03em] mb-4">
        No runs yet.
      </h3>
      <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
        // run <code className="text-accent">grinder run --target ./skills/</code> to populate
      </p>
    </div>
  );
}

function StatusNote({ measured, kept, discarded, crashed, total }: { measured: number; kept: number; discarded: number; crashed: number; total: number }) {
  const items: Array<{ label: string; value: number; cls: string }> = [
    { label: "Measured", value: measured, cls: "text-fg" },
    { label: "Kept",     value: kept,     cls: "text-good" },
    { label: "Discarded",value: discarded,cls: "text-bad" },
    { label: "Crashed",  value: crashed,  cls: "text-warn" },
  ];
  return (
    <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-rule pt-4">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted">// Status legend</span>
      {items.map((it) => (
        <span key={it.label} className="font-mono text-[10px] uppercase tracking-[0.12em]">
          <span className={`tabular-nums font-bold ${it.cls}`}>{it.value}</span>
          <span className="text-muted ml-1">{it.label}</span>
        </span>
      ))}
      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted ml-auto">
        n={total}
      </span>
    </div>
  );
}

function RunsView({
  entries,
  onOpenSkill,
}: {
  entries: Array<HistoryEntry & { skill: string }>;
  onOpenSkill: (name: string) => void;
}) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b-2 border-fg">
          <Th>Run</Th>
          <Th>Skill</Th>
          <Th>Value</Th>
          <Th>Status</Th>
          <Th>When</Th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, i) => (
          <tr key={`${e.run}-${e.skill}-${i}`} className="border-b border-rule hover:bg-[var(--hover)]">
            <Td mono>{String(e.run).padStart(3, "0")}</Td>
            <Td mono accent>
              <button
                type="button"
                onClick={() => onOpenSkill(e.skill)}
                className="text-accent hover:underline text-left"
              >
                {e.skill}
              </button>
            </Td>
            <Td mono>
              <span className={`${scoreClassText(e.value)} font-bold text-t-20`}>{e.value}</span>
            </Td>
            <Td>
              <StatusPill status={e.status} />
            </Td>
            <Td mono muted>{relativeDays(e.timestamp)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SkillsView({
  history,
  onOpenSkill,
}: {
  history: SkillHistory[];
  onOpenSkill: (name: string) => void;
}) {
  const sorted = useMemo(
    () => [...history].sort((a, b) => b.entries.length - a.entries.length),
    [history]
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {sorted.map((h) => {
        const sortedEntries = [...h.entries].sort((a, b) => a.run - b.run);
        const first = sortedEntries[0];
        const last = sortedEntries[sortedEntries.length - 1];
        const delta = last && first ? last.value - first.value : 0;
        return (
          <article key={h.skill} className="border border-fg p-5 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[4px_4px_0_0_var(--fg)] transition-all duration-[80ms]">
            <div className="flex items-baseline justify-between mb-3">
              <button
                type="button"
                onClick={() => onOpenSkill(h.skill)}
                className="font-display font-bold text-t-20 tracking-[-0.02em] text-left hover:underline truncate flex-1"
              >
                {h.skill}
              </button>
              <span className={`font-mono text-[12px] tabular-nums font-bold shrink-0 ml-2 ${delta > 0 ? "text-good" : delta < 0 ? "text-bad" : "text-muted"}`}>
                {delta > 0 ? "+" : ""}{delta}
              </span>
            </div>
            <div className="flex items-end gap-1 h-12 mb-3">
              {sortedEntries.map((e, i) => {
                const pct = Math.round(e.value);
                const last = i === sortedEntries.length - 1;
                const bgClass = scoreClass(e.value);
                const bgClassName = bgClass === "good" ? "bg-good" : bgClass === "warn" ? "bg-warn" : "bg-bad";
                return (
                  <div
                    key={i}
                    title={`Run ${e.run}: ${e.value}`}
                    className={`flex-1 min-w-[3px] ${last ? "bg-accent" : bgClassName}`}
                    style={{ height: `${pct}%` }}
                  />
                );
              })}
            </div>
            <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
              <span>{sortedEntries.length} runs</span>
              <span>best: <span className="text-fg font-medium">{h.best ?? "—"}</span></span>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted font-medium">{children}</th>;
}
function Td({ children, mono = false, accent = false, muted = false }: { children: React.ReactNode; mono?: boolean; accent?: boolean; muted?: boolean }) {
  return (
    <td className={`p-3 ${mono ? "font-mono text-[12px] tabular-nums" : ""} ${accent ? "text-accent" : ""} ${muted ? "text-muted" : ""}`}>
      {children}
    </td>
  );
}

function StatusPill({ status }: { status: HistoryEntry["status"] }) {
  const map: Record<string, { label: string; cls: string }> = {
    keep:     { label: "KEEP",     cls: "bg-good text-bg" },
    discard:  { label: "DISCARD",  cls: "bg-bad text-bg" },
    measured: { label: "MEASURED", cls: "bg-fg text-bg" },
    crash:    { label: "CRASH",    cls: "bg-warn text-fg" },
  };
  const m = (status && map[status]) || { label: "UNKNOWN", cls: "bg-muted text-bg" };
  return (
    <span className={`inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
}
